import React from 'react';
import PropTypes from 'prop-types';

import AnchorLink from '../anchorLink';
import ChatPanel from './chatPanel';
import ContactPanel from './contactPanel';
import DealBuilderPanel from './dealBuilderPanel';
import DeliveryOptionsModal from '../deliveryOptionsModal';
import DeliveryOptionsPanel from './deliveryOptionsPanel';
import EnquiryPanel from './EnquiryPanel';
import ImageThumbnail from '@components/application/imageThumbnail';
import ImageThumbnailExperiment from '@components/application/imageThumbnailExperiment';
import LiveChatButton from './liveChatButton';
import ProductPricing from './productPricing';
import ProductScarcity from '../productScarcity';
import ReadyToAdvertiseChip from '@components/application/vehicle/readyToAdvertiseChip';
import ShareButton from './shareButton';
import ShortlistButton from '@components/shortlist/shortlistButton';
import StatusBanner from '@components/application/vehicle/statusBanner';
import ValueRangePromotion from '@components/application/vehicle/valueRangePromotion';
import VehicleReserved from '@components/vehicle_search/vehicleReserved';
import VehicleTitle from '@components/application/vehicle/vehicleTitle';
import VideoLinks from '../videoLinks';
import WhatsAppButton from './whatsAppButton';

const ProductHeader = ({
  authenticityToken,
  clickAndCollectAvailable,
  deliveryPreferences,
  eligibleForLowFinanceChip,
  enquiryPath,
  experiments,
  finance,
  livePersonAvailable,
  phoneNumber,
  pricing,
  quotationsAvailable,
  reservationAvailable,
  submitEnquiryParamsForm,
  updateDeliveryPreferences,
  userLocation,
  vehicle,
  whatsAppAvailable,
}) => {
  const shouldShowScarcityMessage = () =>
    reservationAvailable && !vehicle.isReserved && !!vehicle.scarcityValue;

  return (
    <>
      <div className="productheader__primary ch-clearfix md:ch-display--flex md:ch-align-items--center">
        <div className="ac-productheader__image-wrapper js-off--hide">
          <ShortlistButton
            authenticityToken={authenticityToken}
            experiments={experiments}
            stockReference={vehicle.stockReference}
          />
          {vehicle.readyToAdvertise && !vehicle.isReserved ? (
            <ReadyToAdvertiseChip />
          ) : (
            <StatusBanner
              isAwaitingImages={vehicle.isAwaitingImages}
              isReserved={vehicle.isReserved}
            />
          )}
          {experiments.productimage ? (
            <ImageThumbnailExperiment
              height={600}
              imageCount={vehicle.photos.length}
              imagePath={vehicle.photos[0]}
              launchImageGallery
              stockReference={vehicle.stockReference}
              width={800}
            />
          ) : (
            <ImageThumbnail
              height={600}
              imageCount={vehicle.photos.length}
              imagePath={vehicle.photos[0]}
              launchImageGallery
              stockReference={vehicle.stockReference}
              width={800}
            />
          )}

          <VideoLinks
            isPhyronAvailable={vehicle.isPhyronAvailable}
            stockReference={vehicle.stockReference}
            videoWalkaround={vehicle.videoWalkaround}
          />
        </div>
        <noscript>
          {vehicle.photos.length > 0 ? (
            <div className="ac-productheader__image-wrapper">
              <div className="ac-imagethumbnail">
                <img
                  alt=""
                  className="ch-img--responsive"
                  height={600}
                  src={vehicle.photos[0]}
                  width={800}
                />
              </div>
            </div>
          ) : (
            <div className="ac-productheader__image-wrapper">
              <div className="ac-imagethumbnail">
                <div className="ac-imagethumbnail__image-placeholder" />
              </div>
            </div>
          )}
        </noscript>
        <div className="ac-productheader__content-wrapper">
          <div className="ch-row md:ch-display--flex md:ch-flex-direction--column">
            <div className="ch-col--12">
              <div className="md:ch-mb--4">
                <VehicleTitle {...vehicle.title} />
                <ShareButton stockReference={vehicle.stockReference} />
                {shouldShowScarcityMessage() && (
                  <ProductScarcity scarcityValue={vehicle.scarcityValue} />
                )}
                <DeliveryOptionsPanel
                  branchName={vehicle.branch.name}
                  branchUrl={vehicle.branch.url}
                  deliveryPreferences={deliveryPreferences}
                  isDeliveryUnavailable={
                    vehicle.isReserved || !clickAndCollectAvailable
                  }
                  userLocation={deliveryPreferences?.location || userLocation}
                />
                <DeliveryOptionsModal
                  stockReference={vehicle.stockReference}
                  updateDeliveryPreferences={updateDeliveryPreferences}
                  userLocation={userLocation}
                />
              </div>
            </div>
            <div className="md:ch-display--flex">
              <div className="ch-col--12 md:ch-col--6 md:ch-display--flex md:ch-flex-direction--column">
                {vehicle.isReserved && (
                  <VehicleReserved
                    enquiryUrl={vehicle.enquiryUrl}
                    make={vehicle.make}
                    model={vehicle.model}
                  />
                )}
                {!vehicle.isReserved && (
                  <div className="ch-clearfix ac-productheader__pricing-wrapper ac-product__side-column">
                    <ProductPricing
                      {...pricing}
                      isBusinessContractHire={vehicle.isBusinessContractHire}
                    />
                    {finance.hp &&
                      Object.prototype.hasOwnProperty.call(
                        finance.hp,
                        'calculator',
                      ) && (
                        <div className="js-off--hide">
                          <AnchorLink
                            anchorId="finance"
                            data-gtm-track="interaction"
                            data-gtm-track-action="click"
                            data-gtm-track-label="Customise finance link"
                            linkText="Customise this car's finance"
                          />
                          {eligibleForLowFinanceChip && (
                            <div>
                              <span className="ch-mt--2 ch-chip ch-chip--outline ch-chip--violet">
                                Low rate finance available
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                  </div>
                )}
                {vehicle.isValueRange && (
                  <div className="ac-promotion__container">
                    <ValueRangePromotion />
                  </div>
                )}
                {finance.pcp &&
                  !Object.prototype.hasOwnProperty.call(
                    finance.pcp,
                    'calculator',
                  ) &&
                  !vehicle.isReserved && (
                    <div className="ch-mt--2 ac-promotion__container">
                      <AnchorLink
                        anchorId="finance"
                        data-gtm-track="interaction"
                        data-gtm-track-action="click"
                        data-gtm-track-label="Finance your car with pcp link"
                        linkText="Finance your car with PCP"
                      />
                    </div>
                  )}
              </div>
              <div className="ch-col--12 md:ch-col--6 md:ch-display--flex md:ch-flex-direction--column">
                <EnquiryPanel
                  enquiryPath={enquiryPath}
                  phoneNumber={phoneNumber}
                  reservationAvailable={reservationAvailable}
                  submitEnquiryParamsForm={submitEnquiryParamsForm}
                  vehicle={vehicle}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="ch-display--flex ch-flex-flow--column-wrap sm:ch-flex-flow--row-wrap ch-pv--1 js-off--hide">
        {vehicle.isEligibleForDealBuilder && quotationsAvailable && (
          <div className="ch-display--flex ch-pv--1 sm:ch-pr--2 sm:ch-width--6">
            <DealBuilderPanel
              financeAvailable={finance?.selectedFinance}
              reservationAvailable={reservationAvailable}
              stockReference={vehicle.stockReference}
            />
          </div>
        )}
        {reservationAvailable && !vehicle.isReserved && (
          <div
            className={`ch-display--flex ch-pv--1 sm:ch-pl--2 sm:ch-width--6 ${
              vehicle.isEligibleForDealBuilder
                ? ''
                : 'sm:ch-width--7 md:ch-col--offset-5'
            }`}
          >
            <ContactPanel
              phoneNumber={phoneNumber}
              submitEnquiryParamsForm={submitEnquiryParamsForm}
            >
              {livePersonAvailable && (
                <ChatPanel whatsAppAvailable={whatsAppAvailable}>
                  <div className="ch-flex--auto ch-pa--1 chat-button-wrapper">
                    <LiveChatButton />
                  </div>
                  {whatsAppAvailable && (
                    <div className="ch-flex--auto ch-pa--1 chat-button-wrapper sm:ch-display--none">
                      <WhatsAppButton
                        branchName={vehicle.branch.name}
                        make={vehicle.make}
                        model={vehicle.model}
                        stockReference={vehicle.stockReference}
                      />
                    </div>
                  )}
                </ChatPanel>
              )}
            </ContactPanel>
          </div>
        )}
      </div>
    </>
  );
};

ProductHeader.propTypes = {
  authenticityToken: PropTypes.string,
  clickAndCollectAvailable: PropTypes.bool,
  deliveryPreferences: PropTypes.object,
  eligibleForLowFinanceChip: PropTypes.bool,
  enquiryPath: PropTypes.string,
  experiments: PropTypes.object,
  finance: PropTypes.object,
  isBlockCar: PropTypes.bool,
  livePersonAvailable: PropTypes.bool,
  phoneNumber: PropTypes.string,
  pricing: PropTypes.object,
  quotationsAvailable: PropTypes.bool,
  reservationAvailable: PropTypes.bool,
  submitEnquiryParamsForm: PropTypes.func,
  updateDeliveryPreferences: PropTypes.func,
  userLocation: PropTypes.string,
  vehicle: PropTypes.shape({
    branch: PropTypes.object,
    enquiryUrl: PropTypes.string,
    isAwaitingImages: PropTypes.bool,
    isBusinessContractHire: PropTypes.bool,
    isEligibleForDealBuilder: PropTypes.bool,
    isPhyronAvailable: PropTypes.bool,
    isReserved: PropTypes.bool,
    isValueRange: PropTypes.bool,
    make: PropTypes.string,
    model: PropTypes.string,
    photos: PropTypes.array,
    readyToAdvertise: PropTypes.bool,
    scarcityValue: PropTypes.number,
    stockReference: PropTypes.string,
    thumbnails: PropTypes.array,
    title: PropTypes.object,
    videoWalkaround: PropTypes.string,
  }),
  whatsAppAvailable: PropTypes.bool,
};

export default ProductHeader;
